/**************************************************************************************************/
/*   File: /src/app/stores/actions/files/files.actions.ts                                         */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 15th 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Fri Apr 05 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import { Action } from '@ngrx/store';

import * as fromShared from '../../../shared';

/**
 * The file action types enum
 */
export enum FileActionTypes {
  /**
   * The file added action's identifier
   */
  FILE_ADDED = '[Authorization][File] File added',
  /**
   * The file removed action's identifier
   */
  FILE_REMOVED = '[Authorization][File] File removed',
  /**
   * The file removed success action's identifier
   */
  FILE_REMOVED_SUCCESS = '[Authorization][File] File removed success',
  /**
   * The file removed fail action's identifier
   */
  FILE_REMOVED_FAIL = '[Authorization][File] File removed fail',
  /**
   * The file clead action's identifier
   */
  FILE_CLEAR = '[Authorization][File] File clear',
  /**
   * The file upload action's identifier
   */
  FILE_UPLOAD = '[Authorization][File] File upload',
  /**
   * The file upload success action's identifier
   */
  FILE_UPLOAD_SUCCESS = '[Authorization][File] File upload success',
  /**
   * The file upload failure action's identifier
   */
  FILE_UPLOAD_FAILURE = '[Authorization][File] File upload fail'
}

/**
 * Action dispatched when a file is ready to be uploaded 
 * and added to the collection of file required for upload
 */
export class FileAddAction implements Action {
  /**
   * The type
   */
  readonly type = FileActionTypes.FILE_ADDED;
  /**
   * The constructor
   * @param payload The payload of files and fileType
   */
  constructor(public payload: { files: Array<fromShared.models.FileModel> }) { }
}
/**
 * Action dispatched when a file is removed
 * from the collection of file required for upload
 */
export class FileRemovedAction implements Action {
  /**
   * The type
   */
  readonly type = FileActionTypes.FILE_REMOVED;
  /**
   * The constructor
   * @param payload The payload is the id of element to remove and fileType
   */
  constructor(public payload: { id: string, isUploaded: boolean }) { }
}

/**
 * Action dispatched when a file is removed also from remote
 */
export class FileRemovedSuccessAction implements Action {
  /**
   * The type
   */
  readonly type = FileActionTypes.FILE_REMOVED_SUCCESS;
  /**
   * The constructor
   * @param payload The payload is the id of element to remove and fileType
   */
  constructor(public payload: { id: string }) { }
}

/**
 * Action dispatched when a file cannot be removed from remote
 */
export class FileRemovedFailAction implements Action {
  /**
   * The type
   */
  readonly type = FileActionTypes.FILE_REMOVED_FAIL;
  /**
   * The constructor
   * @param payload The payload is the message
   */
  constructor(public payload: { message: fromShared.models.Message }) { }
}

/**
 * Action dispatched when the collection of file required for upload
 * is resetted
 */
export class FileClearAction implements Action {
  /**
   * The type
   */
  readonly type = FileActionTypes.FILE_CLEAR;
  /**
   * The constructor
   * @param payload The payload is the context
   */
  constructor(public payload: { files: fromShared.models.FileModel[] }) { }
}

/**
 * Action dispatched when user wants to upload all the files prepared in the collection
 * with other actions previously described
 */
export class FileUploadAction implements Action {
  /**
   * The type
   */
  readonly type = FileActionTypes.FILE_UPLOAD;
  /**
   * The constructor
   * @param payload The payload is the single file and the fileType
   */
  constructor(public payload: { file: fromShared.models.FileModel }) { }
}
/**
 * Action dispatched when file are correctly uploaded to the middleware
 */
export class FileUploadActionSuccess implements Action {
  /**
   * The type
   */
  readonly type = FileActionTypes.FILE_UPLOAD_SUCCESS;
  /**
   * The constructor
   * @param payload The payload is the single file and the  fileType: fromShared.enums.FileType
   */
  constructor(public payload: {
    file: fromShared.models.FileModel,
    documentId: fromShared.models.LocalDocumentId
  }) { }
}
/**
 * Action dispatched when file upload throws an exception
 */
export class FileUploadActionFail implements Action {
  /**
   * The type
   */
  readonly type = FileActionTypes.FILE_UPLOAD_FAILURE;

  /**
   * The constructor
   * @param payload The error to display and the context
   */
  constructor(public payload: { message: fromShared.models.Message }) { }
}

/**
 * The file action type
 */
export type FileActions =
  FileAddAction |
  FileRemovedAction |
  FileRemovedSuccessAction |
  FileRemovedFailAction |
  FileClearAction |
  FileUploadAction |
  FileUploadActionSuccess |
  FileUploadActionFail;
